 
import { faPlus, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import Menu from '@mui/material/Menu';
import { FastField, useField } from 'formik';
import { MouseEvent, memo, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { FormLabel } from 'src/components/FormLabel';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Skeleton } from 'src/components/Skeleton';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { useSession } from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { FeatureFlag } from 'src/models/platform/_types';
import { isHeadTrainer } from 'src/models/user/utils';
import { useTasks } from 'src/pages/TimeTracking/useTasks';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

import { useTrainerSettings } from './useTrainerSettings';


const gridItemCommonSize = {
  xs: 12,
  sm: 6,
  md: 4,
};

export const TrainerPermissions = memo(
  ({ namePrefix }: { namePrefix: string }) => {
    const { t: tSummary } = useTranslation();
    const { t } = useTranslation('translation', {
      keyPrefix: 'staff.settings',
    });

    const { session, user } = useSession();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };

    const {
      additionalPermissions,
      locationPermissions,
      connectedAppsPermissions,
      fbmPermissions,
      permissions,
      cancelVisitPermissions,
      clientPackagePermissions,
      subTrainerPermissionOptions,
      clockPermissionOptions,
      inventoryPermissionOptions,
      billingInfoPermissionOptions,
      clientManagementTabs,
      dashboardBoxes,
      dashboardNotifications,
      emailOptions,
      adminEmails,
      allReports,
      posConfigurationPermissions,
      kpiBlueprints,
      chartBlueprints,
      kpiBlueprintsLoading,
      chartBlueprintsLoading,
    } = useTrainerSettings();

    const { tasks } = useTasks();

    // vs is no longer needed - all values accessed via useField in individual components

    const options = useMemo(() => {
      return additionalPermissions.map((permission) => ({
        value: permission.id as unknown as string,
        label: permission.name,
      }));
    }, [additionalPermissions]);

    // Check if any data is still loading
    const isLoading = kpiBlueprintsLoading || chartBlueprintsLoading;

    // Show skeleton while any critical data is loading
    if (isLoading) {
      return (
        <Grid container spacing={2}>
          {Array.from({ length: 8 }).map((_, index) => (
            <Grid size={gridItemCommonSize} key={index}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          ))}
        </Grid>
      );
    }

    // No more useField hooks in main component to prevent re-renders

    return (
      <>
        <Grid container spacing={2}>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={options}
              label={t('workoutPlans')}
              name={`${namePrefix}.workoutPlansRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={options}
              label={t('routines')}
              name={`${namePrefix}.routinesRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={options}
              label={t('exercises')}
              name={`${namePrefix}.exercisesRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={locationPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('clients')}
              name={`${namePrefix}.clientsRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={connectedAppsPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('connectedApps')}
              name={`${namePrefix}.connectedAppsRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={additionalPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('assignments')}
              name={`${namePrefix}.assessmentsRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={locationPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('messages')}
              name={`${namePrefix}.messagesRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={additionalPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('resources')}
              name={`${namePrefix}.resourcesRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={additionalPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('videos')}
              name={`${namePrefix}.videosRole`}
              size="medium"
              fast
            />
          </Grid>
          {session.enableFbm && (
            <>
              <Grid size={gridItemCommonSize}>
                <FormSelect
                  options={fbmPermissions.map((permission) => ({
                    value: permission.id as unknown as string,
                    label: permission.name,
                  }))}
                  label={t('scheduling')}
                  name={`${namePrefix}.schedulingRole`}
                  size="medium"
                  fast
                />
              </Grid>
              <Grid size={gridItemCommonSize}>
                <FormSelect
                  options={permissions.map((permission) => ({
                    value: permission.id as unknown as string,
                    label: permission.name,
                  }))}
                  label={t('availabilitySchedules')}
                  name={`${namePrefix}.schedulesRole`}
                  size="medium"
                  fast
                />
              </Grid>
              <Grid size={gridItemCommonSize}>
                <FormSelect
                  options={cancelVisitPermissions.map((permission) => ({
                    value: permission.id as unknown as string,
                    label: permission.name,
                  }))}
                  label={t('cancelVisits')}
                  name={`${namePrefix}.cancelVisitsRole`}
                  size="medium"
                  fast
                />
              </Grid>
              <Grid size={gridItemCommonSize}>
                <FormSelect
                  options={clientPackagePermissions.map((permission) => ({
                    value: permission.id as unknown as string,
                    label: permission.name,
                  }))}
                  label={t('clientMemberships')}
                  name={`${namePrefix}.clientPackagesRole`}
                  size="medium"
                  fast
                />
              </Grid>
            </>
          )}
          <Grid size={gridItemCommonSize}>
            <Stack direction="row" alignItems="center" sx={{ mb: 1, gap: 0.5 }}>
              <FormLabel>{t('groups')}</FormLabel>
              <Tooltip
                title={
                  <Trans
                    i18nKey="staff.settings.groupWarning"
                    values={{
                      subTrainer: tSummary('trainer.other').toLowerCase(),
                      group: tSummary('group.other').toLowerCase(),
                    }}
                    components={{ b: <strong /> }}
                  />
                }
              >
                <Icon
                  icon={faQuestionCircle}
                  size="small"
                />
              </Tooltip>
            </Stack>
            <FormSelect
              options={additionalPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              name={`${namePrefix}.groupsRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={subTrainerPermissionOptions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('manageStaff')}
              name={`${namePrefix}.subtrainerRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={clockPermissionOptions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('manageTimecard')}
              name={`${namePrefix}.clockRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={posConfigurationPermissions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('posRole')}
              name={`${namePrefix}.posRole`}
              size="medium"
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={inventoryPermissionOptions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('manageInventory')}
              name={`${namePrefix}.inventoryRole`}
              size="medium"
              fast
            />
          </Grid>
          <Grid size={gridItemCommonSize}>
            <FormSelect
              options={billingInfoPermissionOptions.map((permission) => ({
                value: permission.id as unknown as string,
                label: permission.name,
              }))}
              label={t('canViewBillingInfo')}
              name={`${namePrefix}.canViewBillingInfoRole`}
              size="medium"
              fast
            />
          </Grid>
        </Grid>
        <Stack direction="column" spacing={3} mt={4}>
          <ClientManagementChecker namePrefix={namePrefix}>
            <ClientManagementTabsSelect
              namePrefix={namePrefix}
              clientManagementTabs={clientManagementTabs}
              t={t}
              tSummary={tSummary}
            />
          </ClientManagementChecker>
          {hasFeatureFlag(session, FeatureFlag.NewDashboard) ? (
            <>
              <Stack spacing={1}>
                <Box>
                  <HtmlTypography sx={{ mr: 0.5 }} component="span">
                    {t('dashboardKpis')}
                  </HtmlTypography>
                  <RouterLink to="/dashboard" target="_blank">
                    {t('dashboard')}
                  </RouterLink>
                </Box>
                <DashboardKpisSelect
                  namePrefix={namePrefix}
                  kpiBlueprints={kpiBlueprints}
                  kpiBlueprintsLoading={kpiBlueprintsLoading}
                />
              </Stack>
              <Stack spacing={1}>
                <Box>
                  <HtmlTypography sx={{ mr: 0.5 }} component="span">
                    {t('dashboardCharts')}
                  </HtmlTypography>
                  <RouterLink to="/dashboard" target="_blank">
                    {t('dashboard')}
                  </RouterLink>
                </Box>
                <DashboardChartsSelect
                  namePrefix={namePrefix}
                  chartBlueprints={chartBlueprints || []}
                  chartBlueprintsLoading={chartBlueprintsLoading}
                />
              </Stack>
            </>
          ) : (
            <>
              <DashboardBoxesSelect
                namePrefix={namePrefix}
                dashboardBoxes={dashboardBoxes}
                t={t}
                handleClick={handleClick}
              />
              <NotificationsChecker namePrefix={namePrefix}>
                <DashboardNotificationsSelect
                  namePrefix={namePrefix}
                  dashboardNotifications={dashboardNotifications}
                  t={t}
                />
              </NotificationsChecker>
            </>
          )}

          {emailOptions.length > 0 && (
            <EmailsSelect
              namePrefix={namePrefix}
              emailOptions={emailOptions}
              t={t}
              tSummary={tSummary}
            />
          )}

          <NotificationsChecker namePrefix={namePrefix}>
            <AdminEmailsSelect
              namePrefix={namePrefix}
              adminEmails={adminEmails}
              t={t}
              tSummary={tSummary}
              user={user}
            />
          </NotificationsChecker>

          {hasFeatureFlag(session, FeatureFlag.TimeTracking) && (
            <TasksSelect
              namePrefix={namePrefix}
              tasks={tasks}
              t={t}
              tSummary={tSummary}
            />
          )}

          <ReportsSelect
            namePrefix={namePrefix}
            allReports={allReports}
            t={t}
            tSummary={tSummary}
            user={user}
          />
        </Stack>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          {[t('seeExceptionsItem1'), t('seeExceptionsItem2')].map((item) => (
            <ListItem
              key={item}
              onClick={() => {
                handleClose();
              }}
              sx={{
                maxWidth: '400px',
              }}
              primary={
                <HtmlTypography
                  sx={{ mr: 0.5 }}
                  variant="body2"
                  component="span"
                >
                  {item}
                </HtmlTypography>
              }
            />
          ))}
        </Menu>
      </>
    );
  }
);

TrainerPermissions.displayName = 'TrainerPermissions';

// Reusable component for BaseSelect with inverted field logic (stores disabled, displays enabled)
const InvertedFieldBaseSelect = ({
  fieldName,
  allItems,
  getItemId,
  options,
  label,
  sx,
  disabled,
  ...baseSelectProps
}: {
  fieldName: string;
  allItems: any[];
  getItemId: (item: any) => string | number;
  options: any[];
  label?: React.ReactNode;
  sx?: any;
  disabled?: boolean;
  [key: string]: any;
}) => {
  return (
    <FastField name={fieldName}>
      {({ field, form }) => {
        // Calculate enabled values directly without useMemo to avoid hook rule violations
        const disabledIds = new Set(field.value || []);
        const enabledValues = (allItems || [])
          .filter((item) => !disabledIds.has(getItemId(item)))
          .map((item) => getItemId(item));

        return (
          <BaseSelect
            {...baseSelectProps}
            options={options}
            multiple
            label={label}
            sx={sx}
            disabled={disabled}
            value={enabledValues}
            onChange={(enabledItems) => {
              const enabledSet = new Set(enabledItems || []);
              const disabledItems = allItems
                .filter((item) => !enabledSet.has(getItemId(item)))
                .map((item) => getItemId(item));

              form.setFieldValue(fieldName, disabledItems);
            }}
          />
        );
      }}
    </FastField>
  );
};

// Simple component for BaseSelect with direct field access
const DirectFieldBaseSelect = ({
  fieldName,
  options,
  ...baseSelectProps
}: {
  fieldName: string;
  options: any[];
  [key: string]: any;
}) => {
  return (
    <FastField name={fieldName}>
      {({ field, form }) => {
        return (
          <BaseSelect
            {...baseSelectProps}
            options={options}
            value={field.value || []}
            onChange={(value) => {
              form.setFieldValue(fieldName, value || []);
            }}
          />
        );
      }}
    </FastField>
  );
};

// Component to check if notifications should be shown based on form field values
const NotificationsChecker = memo(
  ({
    namePrefix,
    children,
  }: {
    namePrefix: string;
    children: React.ReactNode;
  }) => {
    const [disabledDashboardBoxesField] = useField(
      `${namePrefix}.disabledDashboardBoxes`
    );
    const [clientsRoleField] = useField(`${namePrefix}.clientsRole`);

    const hasNotifications = useMemo(() => {
      return (
        !(disabledDashboardBoxesField.value || []).includes('Notifications') &&
        clientsRoleField.value !== 2
      );
    }, [disabledDashboardBoxesField.value, clientsRoleField.value]);

    return hasNotifications ? <>{children}</> : null;
  }
);

NotificationsChecker.displayName = 'NotificationsChecker';

// Component to check if client management tabs should be shown based on clientsRole
const ClientManagementChecker = memo(
  ({
    namePrefix,
    children,
  }: {
    namePrefix: string;
    children: React.ReactNode;
  }) => {
    const [clientsRoleField] = useField(`${namePrefix}.clientsRole`);

    if (![0, 1, 3].includes(clientsRoleField.value)) {
      return null;
    }

    return <>{children}</>;
  }
);

ClientManagementChecker.displayName = 'ClientManagementChecker';

// Individual BaseSelect components optimized with memo and FastField
// Client Management Tabs Select with conditional rendering
const ClientManagementTabsSelect = memo(
  ({
    namePrefix,
    clientManagementTabs,
    t,
    tSummary,
  }: {
    namePrefix: string;
    clientManagementTabs: any[];
    t: any;
    tSummary: any;
  }) => {
    return (
      <InvertedFieldBaseSelect
        fieldName={`${namePrefix}.disabledTabs`}
        allItems={clientManagementTabs}
        getItemId={(tab) => tab.id}
        options={clientManagementTabs.map((permission) => ({
          id: permission.id,
          value: permission.id as unknown as string,
          label: permission.name,
        }))}
        label={
          <HtmlTypography sx={{ mr: 0.5 }} component="span">
            {t('clientManageTabs', {
              subTrainer: tSummary('trainer.one').toLowerCase(),
            })}
          </HtmlTypography>
        }
        sx={{
          '& .MuiInputLabel-root': {
            whiteSpace: 'wrap',
          },
        }}
      />
    );
  }
);

ClientManagementTabsSelect.displayName = 'ClientManagementTabsSelect';

// Dashboard KPIs Select
const DashboardKpisSelect = memo(
  ({
    namePrefix,
    kpiBlueprints,
    kpiBlueprintsLoading,
  }: {
    namePrefix: string;
    kpiBlueprints: any[];
    kpiBlueprintsLoading: boolean;
  }) => {
    return (
      <InvertedFieldBaseSelect
        fieldName={`${namePrefix}.disabledDashboardKpis`}
        allItems={kpiBlueprints}
        getItemId={(kpi) => kpi.id}
        options={(kpiBlueprints || []).map((kpi) => ({
          id: kpi.id,
          value: kpi.id,
          label: kpi.name,
        }))}
        disabled={kpiBlueprintsLoading}
      />
    );
  }
);

DashboardKpisSelect.displayName = 'DashboardKpisSelect';

// Dashboard Charts Select
const DashboardChartsSelect = memo(
  ({
    namePrefix,
    chartBlueprints,
    chartBlueprintsLoading,
  }: {
    namePrefix: string;
    chartBlueprints: any[];
    chartBlueprintsLoading: boolean;
  }) => {
    return (
      <InvertedFieldBaseSelect
        fieldName={`${namePrefix}.disabledDashboardCharts`}
        allItems={chartBlueprints}
        getItemId={(chart) => chart.id}
        options={(chartBlueprints || []).map((chart) => ({
          id: chart.id,
          value: chart.id,
          label: chart.name,
        }))}
        disabled={chartBlueprintsLoading}
      />
    );
  }
);

DashboardChartsSelect.displayName = 'DashboardChartsSelect';

// Dashboard Boxes Select
const DashboardBoxesSelect = memo(
  ({
    namePrefix,
    dashboardBoxes,
    t,
    handleClick,
  }: {
    namePrefix: string;
    dashboardBoxes: any[];
    t: any;
    handleClick: (event: any) => void;
  }) => {
    return (
      <Stack spacing={1}>
        <Box>
          <Box>
            <HtmlTypography sx={{ mr: 0.5 }} component="span">
              {t('dashboardBoxes')}
            </HtmlTypography>
            <Link href={externalLinks.trainerDashboard} target="_blank">
              {t('dashboard')}
            </Link>
          </Box>
          <Link component="button" onClick={handleClick}>
            {t('seeExceptions')}
          </Link>
        </Box>
        <InvertedFieldBaseSelect
          fieldName={`${namePrefix}.disabledDashboardBoxes`}
          allItems={dashboardBoxes}
          getItemId={(box) => box.value}
          options={dashboardBoxes.map((permission) => ({
            id: permission.value,
            value: permission.value,
            label: permission.name,
          }))}
        />
      </Stack>
    );
  }
);

DashboardBoxesSelect.displayName = 'DashboardBoxesSelect';

// Dashboard Notifications Select
const DashboardNotificationsSelect = memo(
  ({
    namePrefix,
    dashboardNotifications,
    t,
  }: {
    namePrefix: string;
    dashboardNotifications: any[];
    t: any;
  }) => {
    const [disabledNotificationsField, , disabledNotificationsHelpers] =
      useField(`${namePrefix}.disabledDashboardNotifications`);

    const dashboardNotificationsValues = useMemo(() => {
      const disabledIds = new Set(disabledNotificationsField.value || []);
      return (dashboardNotifications || [])
        .filter((notification) => !disabledIds.has(notification.value))
        .map((notification) => notification.value);
    }, [dashboardNotifications, disabledNotificationsField.value]);

    return (
      <Stack spacing={1}>
        <Box>
          <HtmlTypography sx={{ mr: 0.5 }} component="span">
            {t('dashboardNotifications')}
          </HtmlTypography>
          <Link href={externalLinks.trainerDashboard} target="_blank">
            {t('dashboard')}
          </Link>
        </Box>
        <BaseSelect
          options={dashboardNotifications.map((permission) => ({
            id: permission.value,
            value: permission.value,
            label: permission.name,
          }))}
          multiple
          value={dashboardNotificationsValues}
          onChange={(enabledNotifications) => {
            const enabledSet = new Set(enabledNotifications || []);
            const disabledNotifications = dashboardNotifications
              .filter((notification) => !enabledSet.has(notification.value))
              .map((notification) => notification.value);

            disabledNotificationsHelpers.setValue(disabledNotifications);
          }}
        />
      </Stack>
    );
  }
);

DashboardNotificationsSelect.displayName = 'DashboardNotificationsSelect';

// Emails Select
const EmailsSelect = memo(
  ({
    namePrefix,
    emailOptions,
    t,
    tSummary,
  }: {
    namePrefix: string;
    emailOptions: any[];
    t: any;
    tSummary: any;
  }) => {
    return (
      <InvertedFieldBaseSelect
        fieldName={`${namePrefix}.disabledEmails`}
        allItems={emailOptions}
        getItemId={(email) => email.value}
        options={emailOptions.map((email) => ({
          id: email.value,
          value: email.value,
          label: email.name,
        }))}
        label={
          <Box>
            <HtmlTypography sx={{ mr: 0.5 }} component="span">
              {t('emails', {
                subTrainer: tSummary('trainer.one').toLowerCase(),
              })}
            </HtmlTypography>
          </Box>
        }
        sx={{
          '& .MuiInputLabel-root': {
            whiteSpace: 'wrap',
          },
        }}
      />
    );
  }
);

EmailsSelect.displayName = 'EmailsSelect';

// Admin Emails Select
const AdminEmailsSelect = memo(
  ({
    namePrefix,
    adminEmails,
    t,
    tSummary,
    user,
  }: {
    namePrefix: string;
    adminEmails: any[];
    t: any;
    tSummary: any;
    user: any;
  }) => {
    return (
      <Stack spacing={1}>
        <Box>
          <HtmlTypography sx={{ mr: 0.5 }} component="span">
            {t('adminEmails', {
              subTrainer: tSummary('trainer.one').toLowerCase(),
            })}
          </HtmlTypography>
          {isHeadTrainer(user) &&
            t('forDescription', {
              type: t('email').toLowerCase(),
            })}
          <Link href={externalLinks.notifications} target="_blank">
            {t('adminEmailsHelperTextLink')}
          </Link>
        </Box>
        <DirectFieldBaseSelect
          fieldName={`${namePrefix}.headTrainerEmails`}
          options={adminEmails.map((email) => ({
            id: email.value,
            value: email.value,
            label: email.name,
          }))}
          multiple
        />
      </Stack>
    );
  }
);

AdminEmailsSelect.displayName = 'AdminEmailsSelect';

// Tasks Select
const TasksSelect = memo(
  ({
    namePrefix,
    tasks,
    t,
    tSummary,
  }: {
    namePrefix: string;
    tasks: any[];
    t: any;
    tSummary: any;
  }) => {
    return (
      <Stack spacing={1}>
        <InvertedFieldBaseSelect
          fieldName={`${namePrefix}.disabledTasks`}
          allItems={tasks}
          getItemId={(task) => task.id.toString()}
          options={tasks.map((task) => ({
            id: task.id.toString(),
            value: task.id.toString(),
            label: task.name,
          }))}
          label={
            <Box>
              <HtmlTypography sx={{ mr: 0.5 }} component="span">
                {t('tasks', {
                  subTrainer: tSummary('trainer.one').toLowerCase(),
                })}
              </HtmlTypography>
            </Box>
          }
          sx={{
            '& .MuiInputLabel-root': {
              whiteSpace: 'wrap',
            },
          }}
        />
      </Stack>
    );
  }
);

TasksSelect.displayName = 'TasksSelect';

// Reports Select
const ReportsSelect = memo(
  ({
    namePrefix,
    allReports,
    t,
    tSummary,
    user,
  }: {
    namePrefix: string;
    allReports: any[];
    t: any;
    tSummary: any;
    user: any;
  }) => {
    return (
      <Stack spacing={1}>
        <Box>
          <HtmlTypography sx={{ mr: 0.5 }} component="span">
            {t('reportsLabel', {
              subTrainer: tSummary('trainer.one').toLowerCase(),
            })}
          </HtmlTypography>
          {isHeadTrainer(user) &&
            t('forDescription', {
              type: t('report').toLowerCase(),
            })}
          <Link href="/ex4/reports/" target="_blank">
            {t('reportsPage')}
          </Link>
        </Box>
        <DirectFieldBaseSelect
          fieldName={`${namePrefix}.reportsRole`}
          options={allReports.map((report) => ({
            id: report.id,
            label: report.name,
          }))}
          multiple
        />
        <FastField name={`${namePrefix}.reportsRole`}>
          {({ form }) => (
            <Link
              component="button"
              onClick={() => {
                form.setFieldValue(
                  `${namePrefix}.reportsRole`,
                  allReports.map((report) => report.id)
                );
              }}
              sx={{
                width: 'fit-content',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Icon icon={faPlus} />
              {t('addAllReports')}
            </Link>
          )}
        </FastField>
      </Stack>
    );
  }
);

ReportsSelect.displayName = 'ReportsSelect';

export default TrainerPermissions;
